import { useRef, useState } from 'react'
import { Check, ImagePlus, Upload, X } from 'lucide-react'
import type { MediaAsset } from '@/api/client'
import { useMedia, useUploadMedia } from '@/lib/queries'
import { cn } from '@/lib/utils'
import { Button } from './Button'
import { Dialog } from './Dialog'
import { errorText } from './ErrorState'

export const mediaFileUrl = (id: number) => `/api/media/${id}/file`

/** A media file as a square thumbnail (a still of a video). */
export function MediaThumb({ asset, className }: { asset: MediaAsset; className?: string }) {
  return asset.Kind === 'image' ? (
    <img src={mediaFileUrl(asset.ID)} alt={asset.Filename} loading="lazy" className={cn('h-full w-full object-cover', className)} />
  ) : (
    <video src={mediaFileUrl(asset.ID)} muted preload="metadata" className={cn('h-full w-full object-cover', className)} />
  )
}

export interface MediaPickerProps {
  /** The chosen media id; 0 = none. */
  value: number
  onChange: (id: number) => void
  /** Names the control for screen readers. */
  label?: string
  /** Pick only images (a post's hero); videos are left out. */
  imagesOnly?: boolean
}

/** Picks one file from the media library: a preview of the chosen one and a
 *  dialog with the grid, an upload and "없음". The write-post forms use it
 *  where they used to list file names in a select. */
export function MediaPicker({ value, onChange, label = '이미지', imagesOnly }: MediaPickerProps) {
  const media = useMedia()
  const upload = useUploadMedia()
  const [open, setOpen] = useState(false)
  const file = useRef<HTMLInputElement>(null)
  const items = (media.data?.items ?? []).filter((m) => !imagesOnly || m.Kind === 'image')
  const chosen = (media.data?.items ?? []).find((m) => m.ID === value)

  const pick = (id: number) => {
    onChange(id)
    setOpen(false)
  }

  return (
    <>
      <div className="flex items-center gap-3">
        <button
          type="button"
          aria-label={`${label} 고르기`}
          onClick={() => setOpen(true)}
          className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-md border border-dashed border-border bg-muted text-muted-foreground hover:border-foreground/30"
        >
          {chosen ? <MediaThumb asset={chosen} /> : <ImagePlus size={20} aria-hidden />}
        </button>
        <div className="min-w-0 flex-1 text-table">
          {chosen ? (
            <>
              <div className="truncate font-medium" title={chosen.Filename}>
                {chosen.Filename}
              </div>
              <div className="mt-1 flex gap-1.5">
                <Button size="sm" onClick={() => setOpen(true)}>
                  바꾸기
                </Button>
                <Button size="sm" variant="ghost" onClick={() => onChange(0)}>
                  빼기
                </Button>
              </div>
            </>
          ) : value ? (
            <span className="text-muted-foreground">미디어 #{value} (라이브러리에 없다)</span>
          ) : (
            <Button size="sm" onClick={() => setOpen(true)}>
              고르기
            </Button>
          )}
        </div>
      </div>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        size="lg"
        title={`${label} 고르기`}
        footer={
          <>
            <input
              ref={file}
              type="file"
              hidden
              multiple
              accept={imagesOnly ? 'image/*' : 'image/*,video/*'}
              onChange={async (e) => {
                const fs = Array.from(e.target.files ?? [])
                e.target.value = ''
                if (fs.length === 0) return
                try {
                  const done = await upload.mutateAsync(fs)
                  if (done.length === 1) pick(done[0].ID)
                } catch {
                  /* shown under the grid */
                }
              }}
            />
            <Button icon={Upload} loading={upload.isPending} onClick={() => file.current?.click()} className="mr-auto">
              새로 올리기
            </Button>
            <Button onClick={() => setOpen(false)}>닫기</Button>
          </>
        }
      >
        <div className="max-h-[55vh] overflow-y-auto pb-1">
          {upload.error != null && <p className="mb-2 text-table text-status-failed">{errorText(upload.error)}</p>}
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
            <button
              type="button"
              onClick={() => pick(0)}
              aria-pressed={value === 0}
              className={cn(
                'flex aspect-square flex-col items-center justify-center gap-1 rounded-md border text-meta text-muted-foreground hover:bg-muted',
                value === 0 ? 'border-foreground' : 'border-border',
              )}
            >
              <X size={16} aria-hidden />
              없음
            </button>
            {items.map((m) => (
              <button
                key={m.ID}
                type="button"
                onClick={() => pick(m.ID)}
                aria-pressed={value === m.ID}
                title={m.Filename}
                className={cn(
                  'relative aspect-square overflow-hidden rounded-md border-2 bg-muted',
                  value === m.ID ? 'border-foreground' : 'border-transparent hover:border-border',
                )}
              >
                <MediaThumb asset={m} />
                {value === m.ID && (
                  <span className="absolute right-1 top-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-foreground text-background">
                    <Check size={12} aria-hidden />
                  </span>
                )}
                <span className="absolute inset-x-0 bottom-0 truncate bg-black/55 px-1.5 py-0.5 text-left text-micro text-white">
                  {m.Filename}
                </span>
              </button>
            ))}
          </div>
          {media.data && items.length === 0 && (
            <p className="mt-3 text-table text-muted-foreground">라이브러리가 비어 있다. 아래에서 올린다.</p>
          )}
        </div>
      </Dialog>
    </>
  )
}
